/**
 * pages/wishlist-vibe.tsx
 *
 * /wishlist/:vibe — pending item list for one vibe with FAB to add.
 * Done items shown in a collapsed "Done" section below pending.
 * Long-press → context menu, tap → detail sheet.
 * "Flies into jar" animation triggered on mark-done.
 */
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { WishlistVibe, WishlistItem } from '@just-us/shared'
import {
  useWishlistByVibe,
  useCreateWishlistItem,
  useMarkWishlistDone,
  useNudgeWishlistItem,
  useDeleteWishlistItem,
  useUpdateWishlistItem,
} from '../hooks/use-wishlist'
import { useCouple } from '../hooks/use-couple'
import { getAuthPayload } from '../store/auth'
import BottomNav from '../components/bottom-nav'
import WishlistItemRow from '../components/wishlist/wishlist-item-row'
import ContextMenuSheet from '../components/wishlist/context-menu-sheet'
import ItemDetailSheet from '../components/wishlist/item-detail-sheet'
import QuickAddSheet from '../components/wishlist/quick-add-sheet'
import DoneAnimation from '../components/wishlist/done-animation'
import { VIBE_META } from '../components/wishlist/vibe-chip'

const VALID_VIBES: WishlistVibe[] = ['wander', 'watch', 'taste', 'weekend', 'try', 'together']

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const ItemRowSkeleton = () => (
  <div className="flex items-center gap-3 py-3 border-b border-[#F9E0E8]">
    <div className="w-[26px] h-[26px] rounded-full bg-[#F4C0D1] flex-shrink-0 animate-shimmer" />
    <div className="flex-1 flex flex-col gap-1">
      <div className="h-[15px] bg-[#F9E0E8] rounded-full w-3/4 animate-shimmer" />
      <div className="h-[11px] bg-[#F9E0E8] rounded-full w-1/2 animate-shimmer" />
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function WishlistVibePage() {
  const navigate = useNavigate()
  const { vibe } = useParams<{ vibe: string }>()
  const safeVibe = (VALID_VIBES.includes(vibe as WishlistVibe) ? vibe : 'wander') as WishlistVibe
  const meta = VIBE_META[safeVibe]

  const [showAdd, setShowAdd] = useState(false)
  const [contextItem, setContextItem] = useState<WishlistItem | null>(null)
  const [detailItem, setDetailItem] = useState<WishlistItem | null>(null)
  const [editItem, setEditItem] = useState<WishlistItem | null>(null)
  const [showDoneSection, setShowDoneSection] = useState(false)
  const [doneAnimItem, setDoneAnimItem] = useState<WishlistItem | null>(null)

  const { data, isLoading } = useWishlistByVibe(safeVibe)
  const { data: coupleData } = useCouple()
  const createItem = useCreateWishlistItem()
  const markDone = useMarkWishlistDone()
  const nudgeItem = useNudgeWishlistItem()
  const deleteItem = useDeleteWishlistItem()
  const updateItem = useUpdateWishlistItem()

  const payload = getAuthPayload()
  const users = coupleData?.data?.users ?? []
  const partner = users.find((u) => u.id !== payload?.user_id)
  const partnerNickname = partner?.nickname ?? 'your partner'

  const allItems = data?.data ?? []
  const pendingItems = allItems.filter((i) => !i.doneAt)
  const doneItems = allItems.filter((i) => i.doneAt)

  const handleMarkDone = (item: WishlistItem) => {
    // Trigger fly-to-jar animation before mutating
    if (!item.doneAt) {
      setDoneAnimItem(item)
    }
    markDone.mutate({ id: item.id, vibe: safeVibe })
  }

  const handleNudge = (item: WishlistItem) => {
    nudgeItem.mutate({ id: item.id, vibe: safeVibe })
  }

  const handleDelete = (item: WishlistItem) => {
    deleteItem.mutate({ id: item.id, vibe: safeVibe })
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate('/wishlist')}
            className="w-[36px] h-[36px] rounded-full bg-[#FBEAF0] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-[20px]">{meta.icon}</span>
            <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
              {meta.label}
            </span>
          </div>
          <div className="w-[36px]" />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-5 pt-2 pb-[88px]">
        {isLoading ? (
          <div>
            {Array.from({ length: 4 }).map((_, i) => (
              <ItemRowSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            {/* Pending items */}
            {pendingItems.length === 0 && doneItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center pt-16 gap-3 animate-fade-in">
                <span className="text-[48px] animate-float">{meta.icon}</span>
                <p className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
                  Nothing here yet
                </p>
                <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] text-center px-8">
                  Be the first to add something to {meta.label.toLowerCase()}.
                </p>
                <button
                  type="button"
                  onClick={() => setShowAdd(true)}
                  className="mt-2 h-[44px] px-6 rounded-full font-['DM_Sans'] text-[14px] font-medium text-white shadow-[0_4px_16px_rgba(0,0,0,0.15)] active:scale-95 transition-all duration-150 touch-manipulation"
                  style={{ background: meta.accent }}
                >
                  Add first item
                </button>
              </div>
            ) : (
              <>
                {pendingItems.length === 0 && (
                  <div className="pt-8 pb-4 text-center">
                    <p className="font-['DM_Sans'] text-[14px] text-[#B89AA2]">
                      All done! 🎉
                    </p>
                  </div>
                )}
                {pendingItems.map((item) => (
                  <WishlistItemRow
                    key={item.id}
                    item={item}
                    onToggleDone={handleMarkDone}
                    onOpenDetail={setDetailItem}
                    onOpenContextMenu={setContextItem}
                  />
                ))}

                {/* Done section */}
                {doneItems.length > 0 && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setShowDoneSection(!showDoneSection)}
                      className="flex items-center gap-2 mb-2 touch-manipulation"
                    >
                      <span className="font-['DM_Sans'] text-[12px] text-[#B89AA2]">
                        {doneItems.length} done
                      </span>
                      <span
                        className="font-['DM_Sans'] text-[12px] text-[#B89AA2] transition-transform duration-150"
                        style={{ display: 'inline-block', transform: showDoneSection ? 'rotate(90deg)' : undefined }}
                      >
                        ›
                      </span>
                    </button>
                    {showDoneSection && (
                      <div className="opacity-60 animate-fade-in">
                        {doneItems.map((item) => (
                          <WishlistItemRow
                            key={item.id}
                            item={item}
                            onToggleDone={handleMarkDone}
                            onOpenDetail={setDetailItem}
                            onOpenContextMenu={setContextItem}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      {/* FAB */}
      <button
        type="button"
        onClick={() => setShowAdd(true)}
        className="
          fixed bottom-[80px] right-5
          w-[56px] h-[56px] rounded-full
          text-white text-[24px]
          shadow-[0_4px_20px_rgba(0,0,0,0.2)]
          flex items-center justify-center
          active:scale-90 transition-all duration-150
          touch-manipulation z-30
        "
        style={{ background: meta.accent }}
        aria-label="Add item"
      >
        +
      </button>

      <BottomNav />

      {/* Quick-add sheet */}
      {showAdd && (
        <QuickAddSheet
          defaultVibe={safeVibe}
          allowVibeChange={false}
          isSubmitting={createItem.isPending}
          onAdd={(data) => {
            createItem.mutate(data, { onSuccess: () => setShowAdd(false) })
          }}
          onClose={() => setShowAdd(false)}
        />
      )}

      {/* Edit sheet */}
      {editItem && (
        <QuickAddSheet
          defaultVibe={editItem.vibe as WishlistVibe}
          allowVibeChange={false}
          isSubmitting={updateItem.isPending}
          initialTitle={editItem.title}
          initialNote={editItem.note ?? ''}
          initialLinkUrl={editItem.linkUrl ?? ''}
          onAdd={(data) => {
            const { vibe: _v, ...rest } = data as typeof data & { vibe?: WishlistVibe }
            updateItem.mutate(
              { id: editItem.id, vibe: editItem.vibe as WishlistVibe, ...rest },
              { onSuccess: () => setEditItem(null) }
            )
          }}
          onClose={() => setEditItem(null)}
        />
      )}

      {/* Context menu */}
      {contextItem && (
        <ContextMenuSheet
          item={contextItem}
          partnerNickname={partnerNickname}
          isNudging={nudgeItem.isPending}
          isDeletingItem={deleteItem.isPending}
          onNudge={handleNudge}
          onEdit={(item) => { setEditItem(item); setContextItem(null) }}
          onMarkDone={handleMarkDone}
          onDelete={handleDelete}
          onClose={() => setContextItem(null)}
        />
      )}

      {/* Detail sheet */}
      {detailItem && (
        <ItemDetailSheet
          item={detailItem}
          partnerNickname={partnerNickname}
          isNudging={nudgeItem.isPending}
          isDeletingItem={deleteItem.isPending}
          onNudge={handleNudge}
          onEdit={(item) => { setEditItem(item); setDetailItem(null) }}
          onMarkDone={handleMarkDone}
          onDelete={handleDelete}
          onClose={() => setDetailItem(null)}
        />
      )}

      {/* Fly-to-jar animation */}
      {doneAnimItem && (
        <DoneAnimation
          vibe={doneAnimItem.vibe as WishlistVibe}
          onComplete={() => setDoneAnimItem(null)}
        />
      )}
    </div>
  )
}
